/**
 * @file ShopDomainsSettingsPage.tsx
 * @description «Настройки → Домены» кабинета магазина (phase19.28 этап А;
 * shop-cabinet.md «домены — только чтение и заявка»; PL10–PL12).
 *
 * Адреса сайта магазина только для чтения (`GET /admin/shop/domains`):
 * главный, вид (поддомен платформы / свой домен), статус и результат
 * последней проверки — таблица от 1024, строки-карточки на телефоне.
 * Подключает и меняет адреса платформа; здесь — заявка «Подключить свой
 * домен» (подпись — как заголовок окна). Право `SHOP_MANAGE_SETTINGS`
 * проверяет маршрут.
 */

import { Globe, Plus } from "lucide-react";
import { useState } from "react";

import { usePlatformResource } from "../../../hooks/platform/usePlatformResource";
import { shopDomains } from "../../../lib/api-shop-domains";
import { sortDomains } from "../../../lib/platform/shop-domain-status";
import { domainColumns } from "../../../components/platform/shops/domain-columns";
import { RequestDomainDialog } from "../../../components/shop-domains/RequestDomainDialog";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";

export function ShopDomainsSettingsPage() {
  const [requesting, setRequesting] = useState(false);
  const list = usePlatformResource("shop/domains", shopDomains.list, "Не удалось загрузить адреса сайта. Повторите");
  const domains = sortDomains(list.data ?? []);
  const primaryHost = domains.find((d) => d.isPrimary)?.host ?? null;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Домены"
        subtitle="Адреса сайта магазина. Подключает их платформа"
        action={
          <Button variant="brand" onClick={() => setRequesting(true)}>
            <Plus className="size-4" aria-hidden />
            Подключить свой домен
          </Button>
        }
      />
      <ResponsiveList
        aria-label="Адреса сайта"
        rows={domains}
        columns={domainColumns(primaryHost, null)}
        rowKey={(d) => d.id}
        compactRowSize="tall"
        compactWrap
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        empty={{ icon: Globe, title: "Адресов пока нет", description: "Адрес на платформе появится, когда платформа его подключит." }}
      />
      <p className="type-caption text-on-surface-variant">
        Главный адрес — тот, что видят покупатели; остальные переадресуют на него. Сменить главный адрес или отключить адрес можно через
        поддержку платформы.
      </p>
      <RequestDomainDialog open={requesting} onClose={() => setRequesting(false)} onSent={list.reload} />
    </div>
  );
}
